import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Calculator = () => {
  const [poolsPerMonth, setPoolsPerMonth] = useState(0);
  const [filterCleans, setFilterCleans] = useState(0);
  const [saltCellCleans, setSaltCellCleans] = useState(0);
  const [chemicalUpsells, setChemicalUpsells] = useState(0);
  const [customerReferrals, setCustomerReferrals] = useState(0);
  const [technicianReferrals, setTechnicianReferrals] = useState(0);
  const [mobilityStipend, setMobilityStipend] = useState(true);
  const [qualityBonus, setQualityBonus] = useState(false);
  const [retentionCustomers, setRetentionCustomers] = useState(0);
  const [safetyBonus, setSafetyBonus] = useState(false);

  // Calculations
  const baseEarnings = poolsPerMonth * 15;
  const mobilityEarnings = mobilityStipend ? poolsPerMonth * 1.20 : 0;
  const filterEarnings = filterCleans * 40;
  const saltCellEarnings = saltCellCleans * 10;
  const chemicalCommission = chemicalUpsells * 0.15;
  const referralEarnings = (customerReferrals + technicianReferrals) * 100;
  const qualityEarnings = qualityBonus ? 100 : 0;
  const retentionEarnings = retentionCustomers * 50;
  const safetyEarnings = safetyBonus ? 250 : 0;

  const totalMonthly = baseEarnings + mobilityEarnings + filterEarnings + 
                       saltCellEarnings + chemicalCommission + referralEarnings + 
                       qualityEarnings + retentionEarnings;
  
  const totalQuarterly = (totalMonthly * 3) + safetyEarnings;

  return (
    <WikiLayout>
      <WikiPage 
        title="Earnings Calculator" 
        description="Estimate your potential earnings based on your performance"
      >
        <section className="space-y-8">
          <div className="bg-muted/50 border border-border rounded-lg p-6">
            <p className="text-sm text-muted-foreground">
              Use this calculator to estimate your monthly and quarterly earnings. All values are based on 
              the official BlueLux compensation structure. Enter your expected activity levels below.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Base Service Activity</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pools">Number of Pools Serviced per Month</Label>
                <Input
                  id="pools"
                  type="number"
                  min="0"
                  value={poolsPerMonth || ""}
                  onChange={(e) => setPoolsPerMonth(Number(e.target.value))}
                  placeholder="e.g., 100"
                />
                <p className="text-sm text-muted-foreground">Base: $15 net per pool</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobility">Mobility Stipend (Own Vehicle)</Label>
                <div className="flex items-center space-x-2 h-10">
                  <input
                    id="mobility"
                    type="checkbox"
                    checked={mobilityStipend}
                    onChange={(e) => setMobilityStipend(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Using own vehicle (+$1.20 per pool)</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Base Earnings:</span>
                <span className="text-xl font-bold text-primary">${(baseEarnings + mobilityEarnings).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Specialty Services</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="filters">Filter Cleans per Month</Label>
                <Input
                  id="filters"
                  type="number"
                  min="0"
                  value={filterCleans || ""}
                  onChange={(e) => setFilterCleans(Number(e.target.value))}
                  placeholder="e.g., 5"
                />
                <p className="text-sm text-muted-foreground">Pay: $40 per filter clean</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="saltcells">Salt Cell Cleans per Month</Label>
                <Input
                  id="saltcells"
                  type="number"
                  min="0"
                  value={saltCellCleans || ""}
                  onChange={(e) => setSaltCellCleans(Number(e.target.value))}
                  placeholder="e.g., 3"
                />
                <p className="text-sm text-muted-foreground">Pay: $10 per salt cell clean</p>
              </div>
            </div>

            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Specialty Earnings:</span>
                <span className="text-xl font-bold text-primary">${(filterEarnings + saltCellEarnings).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Commissions</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="chemicals">Chemical Upsell Sales ($)</Label>
                <Input
                  id="chemicals"
                  type="number"
                  min="0"
                  value={chemicalUpsells || ""}
                  onChange={(e) => setChemicalUpsells(Number(e.target.value))}
                  placeholder="e.g., 500"
                />
                <p className="text-sm text-muted-foreground">Commission: 15% of sales</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerRefs">Customer Referrals (this month)</Label>
                <Input
                  id="customerRefs"
                  type="number"
                  min="0"
                  value={customerReferrals || ""}
                  onChange={(e) => setCustomerReferrals(Number(e.target.value))}
                  placeholder="e.g., 1"
                />
                <p className="text-sm text-muted-foreground">Bonus: $100 per referral (after 90 days)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="techRefs">Technician Referrals (this month)</Label>
                <Input
                  id="techRefs"
                  type="number"
                  min="0"
                  value={technicianReferrals || ""}
                  onChange={(e) => setTechnicianReferrals(Number(e.target.value))}
                  placeholder="e.g., 0"
                />
                <p className="text-sm text-muted-foreground">Bonus: $100 per referral (after 90 days)</p>
              </div>
            </div>

            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Commission Earnings:</span>
                <span className="text-xl font-bold text-primary">${(chemicalCommission + referralEarnings).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Monthly Bonuses</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="quality">Quality & Professionalism Bonus</Label>
                <div className="flex items-center space-x-2 h-10">
                  <input
                    id="quality"
                    type="checkbox"
                    checked={qualityBonus}
                    onChange={(e) => setQualityBonus(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Earned this month (+$100)</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Requires: 100% app compliance, professional appearance, zero complaints
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="retention">Customer Retention Bonuses</Label>
                <Input
                  id="retention"
                  type="number"
                  min="0"
                  value={retentionCustomers || ""}
                  onChange={(e) => setRetentionCustomers(Number(e.target.value))}
                  placeholder="e.g., 2"
                />
                <p className="text-sm text-muted-foreground">$50 per customer who reaches 12 months</p>
              </div>
            </div>

            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Bonus Earnings:</span>
                <span className="text-xl font-bold text-primary">${(qualityEarnings + retentionEarnings).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Quarterly Bonuses</h2>
            <div className="space-y-2">
              <Label htmlFor="safety">Safety & Reliability Bonus</Label>
              <div className="flex items-center space-x-2 h-10">
                <input
                  id="safety"
                  type="checkbox"
                  checked={safetyBonus}
                  onChange={(e) => setSafetyBonus(e.target.checked)}
                  className="h-4 w-4"
                />
                <span className="text-sm">Earned this quarter (+$250)</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Requires: Zero missed visits, zero accidents, on-time reporting
              </p>
            </div>
          </div>

          <div className="bg-primary/10 border-2 border-primary rounded-lg p-6 mt-8">
            <h3 className="text-2xl font-bold text-foreground mb-6">Estimated Earnings Summary</h3>
            
            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Base + Mobility:</span>
                <span className="font-medium">${(baseEarnings + mobilityEarnings).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Specialty Services:</span>
                <span className="font-medium">${(filterEarnings + saltCellEarnings).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Commissions:</span>
                <span className="font-medium">${(chemicalCommission + referralEarnings).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Monthly Bonuses:</span>
                <span className="font-medium">${(qualityEarnings + retentionEarnings).toFixed(2)}</span>
              </div>
            </div>

            <div className="border-t-2 border-primary/30 pt-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total Monthly Earnings:</span>
                <span className="text-2xl font-bold text-primary">${totalMonthly.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total Quarterly Earnings:</span>
                <span className="text-2xl font-bold text-primary">${totalQuarterly.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-3">Additional Earning Opportunities</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><strong>Tenure Bonuses:</strong> $250 at 6 months, $250 at 12 months, $500 at 24 months</li>
              <li><strong>Tiered Pay Increases:</strong> Up to $18 net per pool for top performers</li>
              <li><strong>Route Coverage:</strong> $5 extra per pool when covering another technician's route</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-4 italic">
              Note: This calculator provides estimates only. Actual earnings depend on performance, 
              compliance with service standards, and successful completion of requirements.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Calculator;